import { useEffect, useState } from "react";

type Theme = "light" | "dark";
const STORAGE_KEY = "yayy:theme";

function readInitial(): Theme {
	if (typeof document === "undefined") return "light";
	const stored = window.localStorage.getItem(STORAGE_KEY);
	if (stored === "light" || stored === "dark") return stored;
	const root = document.documentElement;
	return root.getAttribute("data-theme") === "dark" || root.classList.contains("dark")
		? "dark"
		: "light";
}

// The app's CSS uses `html[data-theme="dark"]` selectors (custom theme
// tokens defined in index.css). Tailwind's `dark:` variant also responds
// to the `.dark` class on a parent. Keep both in sync so utility classes
// AND custom CSS variables flip together.
function applyTheme(theme: Theme) {
	const root = document.documentElement;
	root.setAttribute("data-theme", theme);
	root.classList.toggle("dark", theme === "dark");
	root.style.colorScheme = theme;
}

export function useTheme(): { theme: Theme; toggle: () => void } {
	const [theme, setTheme] = useState<Theme>(readInitial);

	useEffect(() => {
		applyTheme(theme);
		window.localStorage.setItem(STORAGE_KEY, theme);
	}, [theme]);

	// Cross-tab sync
	useEffect(() => {
		const onStorage = (e: StorageEvent) => {
			if (e.key === STORAGE_KEY && (e.newValue === "light" || e.newValue === "dark")) {
				setTheme(e.newValue);
			}
		};
		window.addEventListener("storage", onStorage);
		return () => window.removeEventListener("storage", onStorage);
	}, []);

	return {
		theme,
		toggle: () => setTheme((t) => (t === "dark" ? "light" : "dark")),
	};
}
